import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { showError } from '@utils';
import { useQuery } from 'react-query';
import { QUERY_KEYS } from 'constants/queryKeys';
import {
  agentsServices,
  fleetVehiclesServices,
  serviceVehiclesServices,
} from '@services';
import { AGENT_ROLES } from '@constants';
import { getAgentsRequestPayload } from 'hooks/agents/utils';
import {
  handleAgentsData,
  handleComments,
  handleServiceVehicleData,
  normalizeVehicleHistory,
} from './utils';

const ACCEPTABLE_AGENT_ROLES = [
  AGENT_ROLES.isARMTeamLead,
  AGENT_ROLES.isAccidentsSeniorAssociate,
  AGENT_ROLES.isRepairsMaintenanceAssociate,
  AGENT_ROLES.isInsuranceSpecialist,
  AGENT_ROLES.isCustomerSuccessManager,
  AGENT_ROLES.isCustomerSuccessTeamLead,
  AGENT_ROLES.isCustomerSuccessCoordinator,
];

export const useServiceVehicleOperations = () => {
  const router = useRouter();
  const serviceVehicleId = router.query?.serviceVehicleId;

  const serviceVehicleResponse = useQuery(
    [QUERY_KEYS.GET_SERVICE_VEHICLE_KEY, serviceVehicleId],
    () => serviceVehiclesServices.getServiceVehicle(serviceVehicleId),
    {
      enabled: !!serviceVehicleId,
    }
  );

  const serviceVehiclesEnumsResponse = useQuery(
    QUERY_KEYS.GET_SERVICE_VEHICLES_ENUMS_KEY,
    () => serviceVehiclesServices?.getServiceVehiclesEnums(),
    {
      enabled: !!serviceVehicleId,
    }
  );

  const serviceVehicleCommentsResponse = useQuery(
    [QUERY_KEYS.GET_SERVICE_VEHICLE_COMMENTS_KEY, serviceVehicleId],
    () => serviceVehiclesServices.getServiceVehicleComments(serviceVehicleId),
    {
      enabled: !!serviceVehicleId,
    }
  );
  const vehicleInternalIdId =
    serviceVehicleResponse?.data?.data?.vehicle?.internalId;
  const serviceVehicleHistoryResponse = useQuery(
    [QUERY_KEYS.GET_SERVICE_VEHICLE_HISTORY_KEY, vehicleInternalIdId],
    () => fleetVehiclesServices.getVehicleHistory(vehicleInternalIdId),
    {
      enabled: serviceVehicleResponse.isFetched && !!vehicleInternalIdId,
    }
  );
  const history = normalizeVehicleHistory(
    serviceVehicleHistoryResponse?.data || []
  );

  const serviceVehicle = handleServiceVehicleData(
    serviceVehicleResponse?.data?.data,
    serviceVehiclesEnumsResponse?.data?.data?.types
  );

  const agentsResponse = useQuery(
    QUERY_KEYS.GET_AGENTS_KEY,
    () =>
      agentsServices.getFilterAgents({
        params: {
          page: 1,
          pageSize: 1000,
        },
        payload: getAgentsRequestPayload({}),
      }),
    {
      enabled: !!serviceVehicleId,
    }
  );

  const agents = handleAgentsData(agentsResponse?.data?.data);
  const agentsToAssign = agents?.filter((agent) =>
    ACCEPTABLE_AGENT_ROLES.includes(agent?.role)
  );

  useEffect(() => {
    if (serviceVehicleResponse?.isError) {
      showError(serviceVehicleResponse.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceVehicleResponse?.isError]);

  useEffect(() => {
    if (agentsResponse?.isError) {
      showError(agentsResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentsResponse?.isError]);

  useEffect(() => {
    if (serviceVehiclesEnumsResponse?.isError) {
      showError(serviceVehiclesEnumsResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceVehiclesEnumsResponse?.isError]);

  useEffect(() => {
    if (serviceVehicleCommentsResponse?.isError) {
      showError(serviceVehicleCommentsResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceVehicleCommentsResponse?.isError]);

  const comments = useMemo(
    () => handleComments(serviceVehicleCommentsResponse?.data?.data, agents),
    [serviceVehicleCommentsResponse?.data?.data, agents]
  );

  const fetchServiceVehicle = async () => {
    await serviceVehicleResponse?.refetch();
    await serviceVehicleCommentsResponse?.refetch();
  };
  return {
    loading:
      serviceVehicleResponse?.isFetching ||
      agentsResponse?.isFetching ||
      serviceVehiclesEnumsResponse?.isFetching ||
      serviceVehicleCommentsResponse?.isFetching ||
      serviceVehicleHistoryResponse?.isFetching,
    serviceVehicle,
    comments,
    agents,
    agentsToAssign,
    history,
    getServiceVehicle: fetchServiceVehicle,
  };
};
